import axios from 'axios';
import axiosInstance from './axiosInstance';

/**
 * Assessment Orchestration platform client (AO-1.15).
 *
 * Thin wrappers only — no scoring, ranking, or publish-eligibility logic.
 * Domain rules live on the platform and arrive as structured `code` / `errors`.
 */

const BASE = '/assessments';

/**
 * Classify platform errors for UI copy. Never invents eligibility rules.
 *
 * @param {unknown} err
 */
export function classifyAssessmentError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  const code = body.code;
  const errors = Array.isArray(body.errors) ? body.errors : undefined;
  const message = body.message || err?.message || 'Request failed';

  return {
    status,
    code,
    message,
    errors,
    // Present only on RESULTS_NOT_PUBLISHED: when the cohort-wide settle period
    // ends and results can first exist.
    resultsExpectedAt: body.resultsExpectedAt || null,
    tlsRequired: status === 503 && code === 'TLS_REQUIRED',
    notEditable: code === 'NOT_EDITABLE',
    paidOnly: code === 'PAID_ONLY_PROBLEM',
    mixedPlatform: code === 'MIXED_PLATFORM_UNSUPPORTED',
  };
}

export async function listAssessments(params = {}) {
  const { data } = await axiosInstance.get(BASE, { params });
  return data;
}

export async function getAssessment(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}`);
  return data;
}

export async function createAssessment(body) {
  const { data } = await axiosInstance.post(BASE, body);
  return data;
}

export async function updateAssessment(id, body) {
  const { data } = await axiosInstance.put(`${BASE}/${id}`, body);
  return data;
}

export async function setAssessmentProblems(id, problems) {
  const { data } = await axiosInstance.put(`${BASE}/${id}/problems`, { problems });
  return data;
}

/**
 * Advisory marks table for the authoring wizard. Purely for display — the
 * platform re-resolves marks server-side for every problem saved without one,
 * so a stale table here can never become a stored mark.
 */
export async function getAssessmentMarksDefaults(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/marks-defaults`);
  return data;
}

export async function publishCheck(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/publish-check`);
  return data;
}

export async function publishAssessment(id, body = {}) {
  const payload = {};
  if (body?.override) {
    payload.override = {
      acknowledged: body.override.acknowledged === true,
      reason: body.override.reason,
      notes: body.override.notes || '',
    };
  }
  if (['auto', 'manual'].includes(body?.resultsPublication?.mode)) {
    payload.resultsPublication = { mode: body.resultsPublication.mode };
  }
  const { data } = await axiosInstance.post(`${BASE}/${id}/publish`, payload);
  return data;
}

export async function duplicateAssessment(id) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/duplicate`);
  return data;
}

export async function cancelAssessment(id, reason) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/cancel`, { reason });
  return data;
}

export async function closeAssessment(id, { confirm = true, reason } = {}) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/close`, { confirm, reason });
  return data;
}

export async function overrideAttemptScore(assessmentId, attemptId, { newScore, reason }) {
  const { data } = await axiosInstance.post(
    `${BASE}/${assessmentId}/attempts/${attemptId}/score-override`,
    { newScore, reason }
  );
  return data;
}

export async function archiveAssessment(id) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/archive`);
  return data;
}

export async function restoreAssessment(id) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/restore`);
  return data;
}

export async function listAssessmentPolicies(params = {}) {
  const { data } = await axiosInstance.get(`${BASE}/policies`, { params });
  return data;
}

/** AMI-2.3 — create AssessmentPolicy (integrity validated server-side). */
export async function createAssessmentPolicy(body) {
  const { data } = await axiosInstance.post(`${BASE}/policies`, body);
  return data;
}

/** AMI-2.3 — immutable new version of an AssessmentPolicy. */
export async function updateAssessmentPolicy(id, body) {
  const { data } = await axiosInstance.put(`${BASE}/policies/${id}`, body);
  return data;
}

/**
 * AMI-2.3 — current rule pack authoring catalog (VIEW_ASSESSMENTS).
 * Prefer this for the Monitoring step so VIEW can render read-only without MANAGE.
 */
export async function getMonitoringRulePack() {
  const { data } = await axiosInstance.get(`${BASE}/monitoring/rule-pack`);
  return data;
}

/** Live console DTO — platform owns counts, liveness, integrity pills, provisional (AO-1.16). */
export async function getAssessmentMonitor(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/monitor`);
  return data;
}

/** AO-11.6 — faculty cohort summary (server-computed aggregates). */
export async function getAssessmentSummary(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/summary`);
  return data;
}

/** Faculty gradebook rows + question analytics (results-first refresh). */
export async function getAssessmentResults(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/results`);
  return data;
}

/** AO-11.6 — CSV export of per-student summary rows. */
export async function downloadAssessmentSummaryCsv(id) {
  const response = await axiosInstance.get(`${BASE}/${id}/summary/export`, {
    params: { format: 'csv' },
    responseType: 'blob',
  });
  return response.data;
}

/** AO-11.7 — release finalised results to students (manual publication mode). */
export async function publishAssessmentResults(id, { confirm = true } = {}) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/publish-results`, { confirm });
  return data;
}

/**
 * AMI-4.3 / AMI-6.1 — open a MonitoringCase (TRIAGE_MONITORING_CASES).
 * Returns `{ success, data: case, created }` — created false when concurrent open won.
 */
export async function openMonitoringCase(assessmentId, body) {
  const { data } = await axiosInstance.post(
    `${BASE}/${assessmentId}/monitoring-cases`,
    body
  );
  return data;
}

/** Faculty integrity dashboard DTO — computed statuses + separated system health (AO-4.4). */
export async function getAssessmentIntegrityDashboard(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/integrity-dashboard`);
  return data;
}

/** AMI-7.1 — per-assessment rule efficacy (VIEW_ASSESSMENTS; analytics flag). */
export async function getAssessmentRuleEfficacy(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/monitoring/rule-efficacy`);
  return data;
}

/** AMI-7.2 — cohort monitoring distribution (VIEW_ASSESSMENTS; analytics flag). */
export async function getAssessmentCohortDistribution(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/monitoring/cohort-distribution`);
  return data;
}

/** AMI-7.1 — cross-assessment rule efficacy (MANAGE_MONITORING_RULES). */
export async function getCrossAssessmentRuleEfficacy(assessmentIds) {
  const { data } = await axiosInstance.post(`${BASE}/monitoring/rule-efficacy`, {
    assessmentIds,
  });
  return data;
}

/** Per-attempt integrity report — snapshot-backed when finalised (AO-4.6). */
export async function getAssessmentIntegrityReport(assessmentId, attemptId, { provisional = false } = {}) {
  const { data } = await axiosInstance.get(
    `${BASE}/${assessmentId}/attempts/${attemptId}/integrity-report`,
    { params: provisional ? { provisional: '1' } : {} }
  );
  return data;
}

export async function getAssessmentLeaderboard(id, params = {}) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/leaderboard`, { params });
  return data;
}

export async function getAssessmentAttemptDetail(assessmentId, attemptId) {
  const { data } = await axiosInstance.get(`${BASE}/${assessmentId}/attempts/${attemptId}`);
  return data;
}

export async function getAssessmentAttemptTimeline(assessmentId, attemptId) {
  const { data } = await axiosInstance.get(
    `${BASE}/${assessmentId}/attempts/${attemptId}/timeline`
  );
  return data;
}

/** AMI-5.1 / AMI-5.2 / AMI-6.2 — faculty investigation (optional pinned pack). */
export async function getAssessmentInvestigation(
  assessmentId,
  attemptId,
  { rulePackVersion } = {}
) {
  const { data } = await axiosInstance.get(
    `${BASE}/${assessmentId}/attempts/${attemptId}/investigation`,
    {
      params:
        rulePackVersion != null && String(rulePackVersion).trim() !== ''
          ? { rulePackVersion: String(rulePackVersion).trim() }
          : {},
    }
  );
  return data;
}

/** AMI-6.2 — review queue list. */
export async function listMonitoringCases(assessmentId, params = {}) {
  const { data } = await axiosInstance.get(`${BASE}/${assessmentId}/monitoring-cases`, {
    params,
  });
  return data;
}

/** AMI-6.1 / AMI-6.2 — single case + evidence summary. */
export async function getMonitoringCase(assessmentId, caseId) {
  const { data } = await axiosInstance.get(
    `${BASE}/${assessmentId}/monitoring-cases/${caseId}`
  );
  return data;
}

/** AMI-6.1 — add student-disclosable note. */
export async function addMonitoringCaseNote(assessmentId, caseId, body) {
  const { data } = await axiosInstance.post(
    `${BASE}/${assessmentId}/monitoring-cases/${caseId}/notes`,
    { body }
  );
  return data;
}

/**
 * AMI-6.3 — dispose a case (dismissed | noted | escalated).
 * Never voids an attempt (ADR-AMI-006).
 */
export async function disposeMonitoringCase(assessmentId, caseId, payload = {}) {
  const { data } = await axiosInstance.post(
    `${BASE}/${assessmentId}/monitoring-cases/${caseId}/dispose`,
    {
      disposition: payload.disposition,
      dispositionReason: payload.dispositionReason,
    }
  );
  return data;
}

/** AMI-6.4 — student case disclosure (self-scoped; null when absent). */
export async function getMyCaseDisclosure(assessmentId) {
  const { data } = await axiosInstance.get(
    `${BASE}/${assessmentId}/attempt/case-disclosure`
  );
  return data;
}

/** AMI-6.4 — one student response (changes nothing). */
export async function addMyCaseDisclosureResponse(assessmentId, body) {
  const { data } = await axiosInstance.post(
    `${BASE}/${assessmentId}/attempt/case-disclosure/response`,
    { body }
  );
  return data;
}

/** Problem Selection Advisory (Epic 2) — the platform computes, the UI renders. */

export async function getAssessmentAdvisory(id, { force = false } = {}) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/advisory`, {
    params: force ? { force: 'true' } : {},
  });
  return data;
}

/** Prior-solve for a candidate problem BEFORE it is added to the set (AO-2.5). */
export async function probeAssessmentProblem(id, referenceKey) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/advisory/probe`, {
    params: { referenceKey },
  });
  return data;
}

export async function getAssessmentAlternatives(id, referenceKey, limit) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/advisory/alternatives`, {
    params: { referenceKey, ...(limit ? { limit } : {}) },
  });
  return data;
}

/** Record an explicit override of one high-severity advisory warning (ADR-AO-015). */
export async function acknowledgeAssessmentAdvisory(id, { code, referenceKey = null }) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/advisory/acknowledge`, {
    code,
    referenceKey,
  });
  return data;
}

/** Student surfaces (AO-1.18) — eligibility and consent copy come from the platform. */

export async function listMyAssessments(params = {}) {
  const { data } = await axiosInstance.get(`${BASE}/mine`, { params });
  return data;
}

/**
 * AO-6 — the student's own published results, newest first.
 *
 * Distinct from `listMyAssessments`, which describes eligibility and windows
 * and carries no marks. This is the only student list endpoint that returns a
 * score, and its `score` is an object — map it through
 * `utils/assessmentResultAdapter` rather than reading it in a component.
 */
export async function listMyResults(params = {}) {
  const { data } = await axiosInstance.get(`${BASE}/mine/results`, { params });
  return data;
}

export async function getAssessmentInstructions(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/instructions`);
  return data;
}

export async function runAssessmentPreflight(id, body) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/preflight`, body);
  return data;
}

export async function startAssessmentAttempt(id, body) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/attempt`, body);
  return data;
}

export async function resumeAssessmentAttempt(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/attempt`);
  return data;
}

/**
 * AMI-3.1 / AMI-3.2 — silent warning side-channels.
 *
 * Deliberately NOT on `axiosInstance`: its 401 interceptor clears the session and
 * redirects to login. Delivery/ack are fire-and-forget background writes; a stale
 * token must never eject a student mid-exam (AM-20).
 */
function silentAssessmentPost(path) {
  const apiBase = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';
  const token = localStorage.getItem('token');
  return axios.post(`${apiBase}${path}`, null, {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

/** AMI-3.1 — confirm a warning was painted (idempotent, body-less). */
export async function confirmAssessmentWarningDelivered(assessmentId, warningId) {
  const { data } = await silentAssessmentPost(
    `${BASE}/${assessmentId}/attempt/warnings/${encodeURIComponent(warningId)}/delivered`
  );
  return data;
}

/** AMI-3.2 — dismiss / acknowledge (idempotent, body-less; gates nothing). */
export async function acknowledgeAssessmentWarning(assessmentId, warningId) {
  const { data } = await silentAssessmentPost(
    `${BASE}/${assessmentId}/attempt/warnings/${encodeURIComponent(warningId)}/acknowledged`
  );
  return data;
}

export async function submitAssessmentAttemptEarly(id, body = { confirm: true }) {
  const { data } = await axiosInstance.post(`${BASE}/${id}/attempt/submit`, body);
  return data;
}

/** Snapshot-backed own result (AO-1.20). */
export async function getMyAssessmentResult(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/attempt/result`);
  return data;
}

/** Own durable timeline after results publish (AO-1.20). */
export async function getMyAssessmentTimeline(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/attempt/timeline`);
  return data;
}

/** AMI-5.3 — own investigation projection after results publish (student visibility). */
export async function getMyAssessmentInvestigation(id) {
  const { data } = await axiosInstance.get(`${BASE}/${id}/attempt/investigation`);
  return data;
}
